import { Component, OnInit, QueryList, ViewChildren, AfterViewInit } from '@angular/core'
import { ActivatedRoute } from '@angular/router'
import {
  ClaimProcessingRuleODataService,
  ClaimProcessingTypeODataService,
  ClaimTransactionTypeODataService,
  IClaimProcessingRule,
  IClaimProcessingType,
  IClaimTransactionType,
  ProcessorDefinitionBlocklistODataService,
  IProcessorDefinitionBlocklist,
  ProcessorDefinitionValuelistODataService,
  IProcessorDefinitionValuelist,
  ProcessorDefinitionODataService,
  IProcessorDefinition,
  Permissions
} from '@exchange/domain/.'
import { Option } from 'common-components'
import { AppSelectComponent } from 'common-components'
import { forkJoin, Observable, of, Subject } from 'rxjs'
import { ToastrService } from 'ngx-toastr'
import { OutboundDirectoryService } from '@exchange/app/services/outbound-directory.service'
import { catchError, map, takeUntil } from 'rxjs/operators'
import { ProcessorDefinitions } from '@exchange/app/domain/system/processorDefinition'
import {
  BasicUserPermissionsComponentModel,
  selectBasicUserPermissionsComponentModel
} from '@exchange/app/store/selectors/selectors'
import { select, Store } from '@ngrx/store'
import { AppState } from '@exchange/app/store/models/app-state'

interface IModel {
  id?: number
  transactionType: IClaimTransactionType
  processingType: Option
  outboundDirectory: Option
}

@Component({
  selector: 'app-entity-group-edit-processing-rules',
  templateUrl: './entity-group-edit-processing-rules.component.html',
  styleUrls: ['./entity-group-edit-processing-rules.component.scss'],
  standalone: false
})
export class EntityGroupEditProcessingRulesComponent implements OnInit {
  @ViewChildren('processingTypeList') processingTypeLists: QueryList<AppSelectComponent>
  @ViewChildren('outboundDirectoryList') outboundDirectoryLists: QueryList<AppSelectComponent>

  entityGroupId: number
  transactionTypes: IModel[] = []
  processingTypes: IClaimProcessingType[] = []
  processingTypeOptions: Option[] = []
  outboundDirectoryOptions: Option[] = []
  activeOutboundDirectoryOption: Option
  processorDefinitions: IProcessorDefinition[]
  processorDefinitionBlocklist: IProcessorDefinitionBlocklist[]
  highDollarThresholdValue: IProcessorDefinitionValuelist
  timelyFilingDays: IProcessorDefinitionValuelist
  componentModel: BasicUserPermissionsComponentModel
  ngUnsubscribe: Subject<boolean> = new Subject()

  constructor(
    private route: ActivatedRoute,
    private transactionTypesService: ClaimTransactionTypeODataService,
    private processingTypesService: ClaimProcessingTypeODataService,
    private processingRulesService: ClaimProcessingRuleODataService,
    private toastr: ToastrService,
    private outboundDirectoryService: OutboundDirectoryService,
    private processorDefinitionService: ProcessorDefinitionODataService,
    private processorDefinitionBlocklistService: ProcessorDefinitionBlocklistODataService,
    private highDollarThresholdValueService: ProcessorDefinitionValuelistODataService,
    private timelyFilingDaysValueService: ProcessorDefinitionValuelistODataService,
    private store: Store<AppState>
  ) {
    this.store
      .pipe(select(selectBasicUserPermissionsComponentModel), takeUntil(this.ngUnsubscribe))
      .subscribe(m => {
        this.componentModel = m
      })
  }

  ngOnInit() {
    this.entityGroupId = +this.route.snapshot.params['id']

    this.getProcessorDefinitions()

    forkJoin({
      transactionTypes: this.transactionTypesService.query().exec(),
      processingTypes: this.processingTypesService.query().exec(),
      outboundDirectories: this.getOutboundDirectories(),
      existingRecords: this.processingRulesService
        .query()
        .filter(`EntityGroupId eq ${this.entityGroupId}`)
        .exec()
    }).subscribe(({ transactionTypes, processingTypes, outboundDirectories, existingRecords }) => {
      this.processingTypes = processingTypes.data
      this.processingTypeOptions = processingTypes.data.map(x => new Option(x.Id, x.Name, x))

      const defaultProcessingType = this.processingTypeOptions.find(
        x => x.value.Value === 'SendToPayer'
      )

      this.outboundDirectoryOptions = outboundDirectories
      const defaultOutboundDirectory = this.outboundDirectoryOptions[0]

      this.transactionTypes = transactionTypes.data.map(transactionType => {
        const currentRecord = existingRecords.data.find(
          existingRecord => existingRecord.TransactionTypeId === transactionType.Id
        )
        let currentProcessingType = null
        let currentOutboundDirectory = null

        if (currentRecord) {
          currentProcessingType = this.processingTypeOptions.find(
            option => option.id === currentRecord.ClaimProcessingTypeId
          )
          currentOutboundDirectory = this.outboundDirectoryOptions.find(
            option => option.text === currentRecord.OutboundDirectory
          )
        }

        this.activeOutboundDirectoryOption = currentOutboundDirectory || defaultOutboundDirectory

        return {
          id: currentRecord ? currentRecord.Id : null,
          transactionType,
          processingType: currentProcessingType || defaultProcessingType,
          outboundDirectory: currentOutboundDirectory || defaultOutboundDirectory
        } as IModel
      })
    })
  }

  selectProcessingType = (model: IModel) => {
    return (event: any) => {
      model.processingType = event

      const payload = {
        EntityGroupId: this.entityGroupId,
        ClaimProcessingTypeId: model.processingType.id,
        TransactionTypeId: model.transactionType.Id,
        OutboundDirectory: this.activeOutboundDirectoryOption.text
      } as IClaimProcessingRule

      const doneFn = result => {
        model.id = result.Id
        this.toastr.success('Processing rule updated successfully')
      }

      const errorFn = () => {
        this.toastr.error('Processing rule could not be updated')
      }

      if (model.id) {
        payload.Id = model.id
        this.processingRulesService
          .put(payload, model.id)
          .exec()
          .subscribe(doneFn, errorFn)
      } else {
        this.processingRulesService
          .post(payload)
          .exec()
          .subscribe(doneFn, errorFn)
      }
    }
  }

  selectOutboundDirectory = (model: IModel) => {
    return (event: any) => {
      model.outboundDirectory = event

      const payload = {
        EntityGroupId: this.entityGroupId,
        ClaimProcessingTypeId: model.processingType.id,
        TransactionTypeId: model.transactionType.Id,
        OutboundDirectory: model.outboundDirectory.text
      } as IClaimProcessingRule

      const doneFn = result => {
        model.id = result.Id
        this.toastr.success('Processing rule updated successfully')
      }

      const errorFn = () => {
        this.toastr.error('Processing rule could not be updated')
      }

      if (model.id) {
        payload.Id = model.id
        this.processingRulesService
          .put(payload, model.id)
          .exec()
          .subscribe(doneFn, errorFn)
      } else {
        this.processingRulesService
          .post(payload)
          .exec()
          .subscribe(doneFn, errorFn)
      }
    }
  }

  getOutboundDirectories(): Observable<Option[]> {
    return this.outboundDirectoryService.fetchDirectories().pipe(
      map(
        x => x.map((directory, value) => new Option(value, directory)),
        catchError(() => {
          return of([])
        })
      )
    )
  }

  getProcessorDefinitions() {
    this.processorDefinitionBlocklistService
      .query()
      .filter(`EntityGroupId eq ${this.entityGroupId}`)
      .exec()
      .subscribe(response => {
        this.processorDefinitionBlocklist = response.data
        this.processorDefinitionService
          .query()
          .filter(`ProcessingPipelineId ne 3`)
          .orderBy('ProcessingPipelineId, OrderIndex')
          .exec()
          .subscribe(r => (this.processorDefinitions = r.data))
      })

    this.highDollarThresholdValueService
      .query()
      .filter(
        `EntityGroupId eq ${this.entityGroupId} and ProcessorDefinitionId eq ${ProcessorDefinitions.HighDollarThreshold}`
      )
      .exec()
      .subscribe(response => {
        this.highDollarThresholdValue =
          response.data[0] ??
          ({
            ProcessorDefinitionId: ProcessorDefinitions.HighDollarThreshold,
            EntityGroupId: this.entityGroupId,
            Value: '3000'
          } as IProcessorDefinitionValuelist)
      })

    this.timelyFilingDaysValueService
      .query()
      .filter(
        `EntityGroupId eq ${this.entityGroupId} and ProcessorDefinitionId eq ${ProcessorDefinitions.TimelyFiling}`
      )
      .exec()
      .subscribe(response => {
        this.timelyFilingDays =
          response.data[0] ??
          ({
            ProcessorDefinitionId: ProcessorDefinitions.TimelyFiling,
            EntityGroupId: this.entityGroupId,
            Value: '180'
          } as IProcessorDefinitionValuelist)
      })
  }

  onProcessorDefinitionChange(event: Event) {
    const checkBox = event.target as HTMLInputElement
    const blocklist = {
      ProcessorDefinitionId: parseInt(checkBox.getAttribute('pdid'), 10),
      EntityGroupId: this.entityGroupId
    } as IProcessorDefinitionBlocklist
    if (!checkBox.checked) {
      this.processorDefinitionBlocklistService
        .post(blocklist)
        .exec()
        .subscribe(r => {
          this.getProcessorDefinitions()
          this.toastr.success('Entity updated')
        })
    } else {
      const blocklistId = this.processorDefinitionBlocklist.find(
        b => b.ProcessorDefinitionId === blocklist.ProcessorDefinitionId
      ).Id

      this.processorDefinitionBlocklistService
        .delete(blocklistId)
        .exec()
        .subscribe(r => {
          this.getProcessorDefinitions()
          this.toastr.success('Entity updated')
        })
    }
  }

  isProcessorDefinitionBlocklisted(pdid: number) {
    return this.processorDefinitionBlocklist.some(b => b.ProcessorDefinitionId === pdid)
  }

  showAmount(pdid: number) {
    return pdid === ProcessorDefinitions.HighDollarThreshold
  }

  hasHighDollarThresholdEditPermission(): boolean {
    return this.componentModel.userPermissions.includes(Permissions.HighDollarThresholdEdit)
  }

  onSaveAmount(): void {
    let error = false

    if (this.highDollarThresholdValue.Value === '') {
      error = true
      this.toastr.error('Amount is required.')
    }

    if (!error) {
      this.toastr.clear()
      if (this.highDollarThresholdValue.Id) {
        this.highDollarThresholdValueService
          .put(this.highDollarThresholdValue, this.highDollarThresholdValue.Id)
          .exec()
          .subscribe(r => {
            this.toastr.success('High Dollar Threshold amount updated successfully')
          })
      } else {
        this.highDollarThresholdValueService
          .post(this.highDollarThresholdValue)
          .exec()
          .subscribe(r => {
            this.toastr.success('High Dollar Threshold amount updated successfully')
          })
      }
    }
  }

  showDays(pdid: number) {
    return pdid === ProcessorDefinitions.TimelyFiling
  }

  hasTimelyFilingDaysEditPermission(): boolean {
    return this.componentModel.userPermissions.includes(Permissions.TimelyFilingEdit)
  }

  onSaveDays(): void {
    let error = false

    if (this.timelyFilingDays.Value === '') {
      error = true
      this.toastr.error('Days are required.')
    }

    if (!error) {
      this.toastr.clear()
      if (this.timelyFilingDays.Id) {
        this.timelyFilingDaysValueService
          .put(this.timelyFilingDays, this.timelyFilingDays.Id)
          .exec()
          .subscribe(r => {
            this.toastr.success('Timely Filing Days updated successfully')
          })
      } else {
        this.timelyFilingDaysValueService
          .post(this.timelyFilingDays)
          .exec()
          .subscribe(r => {
            this.toastr.success('Timely Filing Days updated successfully')
          })
      }
    }
  }
}
